<template>
  <input class="e-input"
         :type="inputType"
         :class="hasError ? 'is-invalid' : ''"
         :style="inputStyle"
         :name="name"
         ref="input"
         @blur="onBlur"
         @change="change"
         @input="onInput"
         v-on:keyup.enter.prevent="onEnter"
         :placeholder="getPlaceholder"
         v-bind:disabled="disabled"
         v-bind:data-validate-alias="validateAlias"
         v-bind:data-validation-rule="validationRule"
         v-bind:data-mask="mask"
         v-model="model[name]" />
</template>

<script>
  import maskable from '../../mixins/maskable.js'

  // data-* attributes used for debug
  export default {
    mixins: [maskable],
    name: 'e-input',
    data () {
      return {
        error: null,
        lazyValue: null
      }
    },
    props: {
      inputStyle: {
        type: Object,
        default: () => {}
      },
      onChangeValidation: {
        type: Boolean,
        default: false
      },
      formValidator: null,
      model: {
        type: Object,
        required: true
      },
      tabView: {
        type: String,
        default: null
      },
      name: {
        type: String,
        default: null,
        required: true
      },
      validateAlias: {
        type: String,
        default: null
      },
      /* string|email|number|double */
      validationRule: {
        type: String,
        default: 'string'
      },
      validationAdditionalRules: {
        type: Object,
        default: function () {
          return {
            minLen: null,
            maxLen: null,
            minValue: null,
            maxValue: null,
            specialValidate: undefined,
            cantBiggerThanFieldName: null
          }
        }
      },
      required: {
        type: Boolean,
        default: false
      },
      modalVariableName: {
        type: String,
        default: null
      },
      disabled: {
        type: Boolean,
        default: false
      },
      placeholder: {
        type: String,
        default: null
      },
      inputType: {
        type: String,
        default: 'text'
      },
      mask: {
        type: String,
        default: null
      },
      onValidate: {
        type: Function,
        default: (obj) => obj
      }
    },
    computed: {
      getPlaceholder () {
        if (this.placeholder === null) {
          return ''
        }
        return this.placeholder
      },
      hasError () {
        if (this.error) {
          return true
        }
        return false
      }
    },
    created () {
      if (this.formValidator) {
        this.formValidator.subscribeInput(this)
      }
    },
    destroy () {
      if (this.formValidator) {
        this.formValidator.unsubscribeInput(this)
      }
    },
    methods: {
      onEnter ($event) {
        this.$emit('enter', $event)
      },
      onBlur ($event) {
        this.$emit('blur', $event)
      },
      onInput (e) {
        // TODO: fix perfomance
        if (this.mask) {
          this.resetSelections(e.target)
          this.lazyValue = this.maskText(this.unmaskText(e.target.value))
          this.setSelectionRange()
          this.model[this.name] = this.maskText(this.lazyValue)
        }
        this.$emit('input', e)
      },
      change ($event) {
        this.$emit('change', $event)
      },
      getElement () {
        return this.$refs.input
      },
      validate () {
        let msg = ''
        let isEmpty = false
        if (typeof this.model[this.name] === 'undefined') {
          isEmpty = true
        } else if (this.model[this.name] === null) {
          isEmpty = true
        } else if (this.model[this.name].toString().length < 1) {
          isEmpty = true
        }
        if (this.required && isEmpty) {
          msg += 'Заполните поле'
        }

        let emailPattern = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i
        let doublePattern = /^(-?\d*((\.\d{0,}))?(\d))$/i
        let numberPattern = /^-?\d*$/i

        if (isEmpty === false && this.model[this.name]) {
          if (this.validationRule === 'double'
            && !doublePattern.test(this.model[this.name])
          ) {
            msg += 'Укажите число вида 0.000 '

          } else if (this.validationRule === 'number'
            && !numberPattern.test(this.model[this.name])) {
            msg += 'Допустимы только целые числа. '

          } else if (this.validationRule === 'email'
            && !emailPattern.test(this.model[this.name])) {
            msg += 'Неверный адрес email. '
          }
        }

        // additional rules
        if (isEmpty === false && this.model[this.name]) {
          if (this.validationAdditionalRules.minLen && this.model[this.name].length < this.validationAdditionalRules.minLen) {
            msg += 'Нужно указать минимум ' + this.validationAdditionalRules.minLen
            if (this.validationRule === 'number' || this.validationRule === 'double') {
              msg += ' цифр. '
            } else {
              msg += ' символов. '
            }
          } else if (this.validationAdditionalRules.maxLen && this.model[this.name].length > this.validationAdditionalRules.maxLen) {
            msg += 'Допустимо не больше ' + this.validationAdditionalRules.minLen
            if (this.validationRule === 'number' || this.validationRule === 'double') {
              msg += ' цифр.'
            } else {
              msg += ' символов. '
            }
          } else if (this.validationAdditionalRules.specialValidate) {
            msg = this.validationAdditionalRules.specialValidate(msg)
          } else if (!msg && this.validationAdditionalRules.cantBiggerThanFieldName) {
            let revertWithFName = this.validationAdditionalRules.cantBiggerThanFieldName
            if (this.model[revertWithFName] || this.model[revertWithFName] == 0) {
              if (this.model[this.name] > this.model[revertWithFName]) {
                let tmp = this.model[this.name]
                this.model[this.name] = this.model[revertWithFName]
                this.model[revertWithFName] = tmp
              }
            }
          }
        }

        if (isEmpty === false && !msg) {
          if (typeof this.validationAdditionalRules.minValue === 'number') {
            if (this.model[this.name] < this.validationAdditionalRules.minValue) {
              msg += `Минимальное возможное значение ${this.validationAdditionalRules.minValue}. `
            }
          }
          if (typeof this.validationAdditionalRules.maxValue === 'number') {
            if (this.model[this.name] > this.validationAdditionalRules.maxValue) {
              msg += `Максимальное возможное значение ${this.validationAdditionalRules.maxValue}. `
            }
          }
        }

        return this.onValidate({
          error: msg,
          tabView: this.tabView,
          name: this.name,
          modalVariableName: this.modalVariableName,
          el: this.getElement(),
          model: this.model
        })
      }
    }
  }
</script>
